import React, {useEffect, useState} from "react";
import {
    Box,
    Button,
    CardActions,
    CardContent,
    FormControl,
    InputLabel,
    TextField,
    Typography
} from "@mui/material";
import {useDispatch, useSelector} from "react-redux";
import BalanceNumericFormat from "../utils/BalanceNumericFormat";
import {deleteTransaction, saveSetBudgetBalance} from "../../store/transactionSlice";
import {fetchBudgets, selectAllBudgets, selectBudget} from "../../store/budgetSlice";
import {fetchCurrencies, selectCurrencies} from "../../store/currencySlice";
import CurrencyService from "../../services/CurrencyService";
import DateService from "../../services/DateService";
import ViewAndEditFormLayout from "./ViewAndEditFormLayout";
import AmountWithCurrency from "../layout/AmountWithCurrency";
import {useTranslation} from "react-i18next";


const EditChangeBudgetForm = ({changeBudgetTransaction, onCancel}) => {
    const {t} = useTranslation();
    const DEFAULT_ERROR = {description: null, balances: {}, date: null, time: null};

    const dispatch = useDispatch();
    const [transactionDate, setTransactionDate] = useState("");
    const [transactionTime, setTransactionTime] = useState("");
    const [description, setDescription] = useState(changeBudgetTransaction.description || "");
    const budget = useSelector(state => selectBudget(state, changeBudgetTransaction.budget));
    const [balancesAfter, setBalancesAfter] = useState([]);
    const [balancesBefore, setBalancesBefore] = useState([]);
    const [errors, setErrors] = useState(DEFAULT_ERROR);
    const currencies = useSelector(state => selectCurrencies(state));


    useEffect(() => {
        dispatch(fetchCurrencies());
        dispatch(fetchBudgets());
    }, []);

    useEffect(() => {
        setDescription(changeBudgetTransaction.description || "");

        if(budget && changeBudgetTransaction) {
            if(changeBudgetTransaction.id) {
                setBalancesAfter(budget.balances);
                setBalancesBefore(CurrencyService.mergeCurrencies(budget.balances, CurrencyService.negative(changeBudgetTransaction.changes)))
            } else {
                setBalancesAfter(CurrencyService.mergeCurrencies(budget.balances, changeBudgetTransaction.changes));
                setBalancesBefore(budget.balances)
            }
        }

        const dateTime = changeBudgetTransaction.date
            ? DateService.getDate(changeBudgetTransaction.date)
            : new Date();
        const year = dateTime.getFullYear();
        const month = String(dateTime.getMonth() + 1).padStart(2, '0');
        const day = String(dateTime.getDate()).padStart(2, '0');
        setTransactionDate(`${year}-${month}-${day}`);
        const hours = String(dateTime.getHours()).padStart(2, '0');
        const minutes = String(dateTime.getMinutes()).padStart(2, '0');
        setTransactionTime(`${hours}:${minutes}`);

        setErrors(DEFAULT_ERROR);
    }, [changeBudgetTransaction, budget, currencies]);




    const saveWithState = (targetState) => {
        if (validateForm()) {
            const setBalanceToSave = {
                ...changeBudgetTransaction, description: description,
                date: transactionDate + 'T' + transactionTime + ':00',
                budget: budget.id,
                state: targetState,
                changes: CurrencyService.mergeCurrencies(balancesAfter, CurrencyService.negative(balancesBefore))
            }
            return new Promise((resolve) => {
                dispatch(saveSetBudgetBalance(setBalanceToSave, () => resolve("close")));
            });
        }
    }

    const handleSave = () => saveWithState(isDraft ? "DRAFT" : (changeBudgetTransaction?.state || "ACTIVE"));
    const handleApply = () => saveWithState("ACTIVE");
    const handleSaveAsDraft = () => saveWithState("DRAFT");
    const runAndClose = (action) => {
        const result = action();
        if (result && typeof result.then === "function") {
            result.then((saveAction) => {
                if (saveAction === "close" && onCancel) {
                    onCancel();
                }
            });
        }
    };

    const onDelete = () => {
        if (!changeBudgetTransaction.id) return;
        dispatch(deleteTransaction(changeBudgetTransaction, () => onCancel()));
    }

    const validateForm = () => {
        const balanceErrors = {};
        balancesAfter.forEach((balance) => {
            const raw = balance?.value;
            if (raw === null || raw === undefined || `${raw}`.trim() === "") {
                balanceErrors[balance.currency] = "Balance is required";
            }
        });
        const nextErrors = {
            description: null,
            balances: balanceErrors,
            date: !transactionDate ? "Date is required" : null,
            time: !transactionTime ? "Time is required" : null
        };
        setErrors(nextErrors);
        return !nextErrors.date && !nextErrors.time && Object.keys(balanceErrors).length === 0;
    }

    const setCurrencyBalance = (currency, newBalance) => {
        return balancesAfter.map(currencyBalance => currencyBalance.currency === currency ? {currency: currency, balance: newBalance} : currencyBalance);
    }

    const onBalanceChange = (currency, value) => {
        const updatedBalances = balancesAfter.map(balance =>
            balance.currency === currency
                ? { ...balance, value: value }
                : balance
        );
        setBalancesAfter(updatedBalances);
        setErrors(prev => {
            const nextBalances = { ...(prev.balances || {}) };
            delete nextBalances[currency];
            return { ...prev, balances: nextBalances };
        });
    }


    const viewActions = (
        <Box sx={{ position: "relative", width: "100%" }}>
            <Box sx={{ position: "absolute", left: 0 }}>
                <Button variant="outlined" sx={{ mr: "1rem" }} onClick={onCancel}>{t("common.buttons.close")}</Button>
            </Box>
        </Box>
    );

    const viewChanges = changeBudgetTransaction?.changes ?? [];
    const hasViewChanges = viewChanges.length > 0;

    const viewContent = (
        <>
            <CardContent>
                <Box sx={{ m: 2 }}>
                    <Typography variant="body2" color="text.secondary">{t("common.labels.date")}</Typography>
                    <Typography variant="body1">{transactionDate} {transactionTime}</Typography>
                </Box>
                <Box sx={{ m: 2 }}>
                    <Typography variant="body2" color="text.secondary">{t("common.labels.budget")}</Typography>
                    <Typography variant="body1">{budget?.name || "—"}</Typography>
                </Box>
                <Box sx={{ m: 2 }}>
                    <Typography variant="body2" color="text.secondary">{t("common.labels.description")}</Typography>
                    <Typography variant="body1">{description || "—"}</Typography>
                </Box>
                <Box sx={{ m: 2 }}>
                    <Typography variant="body2" color="text.secondary">{t("common.labels.amount")}</Typography>
                    <Typography variant="body1">
                        {hasViewChanges ? (
                            viewChanges.map((change, index) => (
                                <React.Fragment key={`${change.currency ?? "currency"}-${index}`}>
                                    {index > 0 && ", "}
                                    <AmountWithCurrency amount={change.value} currencyId={change.currency} />
                                </React.Fragment>
                            ))
                        ) : (
                            "—"
                        )}
                    </Typography>
                </Box>
            </CardContent>
            <CardActions sx={{ ml: "2rem", mb: "1rem", mt: "1rem", mr: "2rem", height: "4rem" }}>
                <Box sx={{ position: "relative", width: "100%" }}>
                    <Box sx={{ position: "absolute", left: 0 }}>
                        <Button variant="outlined" sx={{ mr: "1rem" }} onClick={onCancel}>{t("common.buttons.close")}</Button>
                    </Box>
                </Box>
            </CardActions>
        </>
    );

    const editContent = ({ onCancel: handleCancel, onSave: handleSaveLayout }) => (
        <>
            <CardContent>
                <Box sx={{ m: 2 }}>
                    <TextField
                        id="date"
                        label={t("common.labels.date")}
                        type="date"
                        value={transactionDate}
                        onChange={e => {
                            setTransactionDate(e.target.value);
                            setErrors(prev => ({ ...prev, date: null }));
                        }}
                        error={!!errors.date}
                        helperText={errors.date}
                        sx={{ width: "50%" }}
                        InputLabelProps={{
                            shrink: true,
                        }}
                    />
                    <TextField
                        id="time"
                        label={t("common.labels.time")}
                        type="time"
                        value={transactionTime}
                        onChange={e => {
                            setTransactionTime(e.target.value);
                            setErrors(prev => ({ ...prev, time: null }));
                        }}
                        error={!!errors.time}
                        helperText={errors.time}
                        InputLabelProps={{
                            shrink: true,
                        }}
                        inputProps={{
                            step: 300,
                        }}
                        sx={{ width: "45%", marginLeft: "12px" }}
                    />
                </Box>

                <FormControl fullWidth sx={{ m: 2 }}>
                    <InputLabel shrink>{t("forms.changeBudget.budgetBalance")}</InputLabel>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
                        {balancesAfter.map(eachBalance => {
                            const eachBalanceCurrency = currencies.find(currency => currency.id === eachBalance.currency);
                            const currencyDelta = CurrencyService.getValue(balancesAfter, eachBalance.currency) - CurrencyService.getValue(balancesBefore, eachBalance.currency);
                            return (
                                <Box key={eachBalanceCurrency ? eachBalanceCurrency.code : "NULL"}>
                                    <TextField
                                        label={eachBalanceCurrency ? eachBalanceCurrency.code : t("forms.changeBudget.withoutCurrency")}
                                        variant="outlined"
                                        sx={{ width: "50%" }}
                                        value={eachBalance.value || ""}
                                        onChange={event => onBalanceChange(eachBalance.currency, event.target.value)}
                                        error={!!errors.balances?.[eachBalance.currency]}
                                        helperText={errors.balances?.[eachBalance.currency] || null}
                                        InputProps={{
                                            inputComponent: BalanceNumericFormat
                                        }}
                                    />
                                    {currencyDelta !== 0 && (
                                        <Box sx={{ display: "inline-block", color: currencyDelta > 0 ? "green" : "red", padding: "18px 0px 0px 8px" }}>
                                            {currencyDelta > 0 ? "+" : ""}{currencyDelta}{eachBalanceCurrency ? " " + eachBalanceCurrency.shortName : ""}
                                        </Box>
                                    )}
                                </Box>
                            );
                        })}
                    </Box>
                </FormControl>
                <FormControl>
                    <Box sx={{ m: 2 }}>
                        <TextField id="description" multiline={true} maxRows={4} minRows={2} value={description}
                                   onChange={(e) => setDescription(e.target.value)}
                                   label={t("common.labels.description")} variant="standard" fullWidth={true} />
                    </Box>
                </FormControl>
            </CardContent>
            <CardActions sx={{ ml: "2rem", mb: "1rem", mt: "1rem", mr: "2rem", height: "4rem" }}>
                <Box sx={{ position: "relative", width: "100%" }}>
                    <Box sx={{ position: "absolute", left: 0 }}>
                        <Button variant="outlined" sx={{ mr: "1rem" }} onClick={handleCancel}>{t("common.buttons.cancel")}</Button>
                        {isDraft ? (
                            <>
                                <Button variant="contained" sx={{ mr: "1rem" }} onClick={() => runAndClose(handleApply)}>
                                    {t("common.buttons.apply")}
                                </Button>
                                <Button variant="outlined" onClick={() => runAndClose(handleSaveAsDraft)}>
                                    Save as draft
                                </Button>
                            </>
                        ) : (
                            <Button variant="contained" onClick={handleSaveLayout}>{t("common.buttons.save")}</Button>
                        )}
                    </Box>
                </Box>
            </CardActions>
        </>
    );

    const isDraft = changeBudgetTransaction?.state === "DRAFT";

    return currencies && changeBudgetTransaction ? (
        <ViewAndEditFormLayout
            editTitle={t("forms.changeBudget.editTitle")}
            viewTitle={t("forms.changeBudget.viewTitle")}
            initialEdit={!changeBudgetTransaction.id || isDraft}
            onCancel={onCancel}
            onSave={handleSave}
            onDelete={changeBudgetTransaction.id ? onDelete : undefined}
            showDeleteInEdit={true}
            viewContent={changeBudgetTransaction.id ? viewContent : null}
            editContent={editContent}
        />
    ) : null
}

export default EditChangeBudgetForm